/**
 * Trace helpers that depend ONLY on `@opentelemetry/api` — the tiny,
 * dependency-free OTel contract package.
 *
 * These are kept SEPARATE from tracing.ts (which pulls the heavy OTel Web SDK:
 * WebTracerProvider, the OTLP exporter, resources, core, …) so that api.ts and
 * the axios instrumentation can create/propagate spans WITHOUT dragging the SDK
 * into the main bundle. The SDK is loaded lazily by `initTracing()` (tracing.ts)
 * only when telemetry is enabled. Until a provider is registered, `getTracer()`
 * returns the global no-op tracer and every helper here is inert:
 *   - startSpan() returns a non-recording span,
 *   - injectTraceContext() injects no `traceparent`,
 * so behaviour is identical to tracing being disabled.
 */
import { context, trace, propagation, type Span } from '@opentelemetry/api';

/** The shared application tracer (resolved lazily against the global provider). */
export function getTracer() {
  return trace.getTracer('nap-frontend');
}

/** Run `fn` with `span` active so any logs emitted inside carry its context. */
export function withSpan<T>(span: Span, fn: () => T): T {
  return context.with(trace.setSpan(context.active(), span), fn);
}

/** Inject W3C trace context for `span` into a header carrier (mutates it). */
export function injectTraceContext(span: Span, headers: Record<string, string>): void {
  withSpan(span, () => propagation.inject(context.active(), headers));
}
